import { Box, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { numberHelper } from '../../utils/numberHelper';
import { CartProductCode, PriceCartItem, RoleType } from '@ace-clublabs/common-membership-api-types';
import { getCartProductName } from '../../utils/helpers';
import { getFormattedDiscountedPrice } from '../../utils/formatters';

type IReviewItem = Partial<PriceCartItem> & {
  title: string | JSX.Element;
  subtitle?: string;
  members?: { memberName: string; memberType: RoleType }[];
  mask?: boolean;
  className?: string;
};

export const ReviewItem = ({
  title,
  subtitle,
  members,
  price,
  discount,
  discountedPrice,
  code,
  mask,
  className,
}: IReviewItem) => {
  const productType = getCartProductName(code);
  const showNoCostAsWaived = code === CartProductCode.ADMISSION || code === CartProductCode.FAMILYCOST;
  const isBenefitLevel = className === 'benefit-level';

  const priceSection =
    price === undefined ? (
      <Typography className={`${productType}-included`}>Included</Typography>
    ) : discount! > 0 ? (
      <Stack direction="row" alignItems="baseline">
        {price ? (
          <Typography
            variant="b3Regular"
            className={`${productType}-full-price`}
            sx={{ textDecorationLine: 'line-through', marginRight: '0.25rem' }}
          >
            {numberHelper.formatMoney(price ?? 0)}
          </Typography>
        ) : (
          <></>
        )}
        {typeof discountedPrice == 'number' ? (
          <Typography
            variant="b1Regular"
            className={`${productType}-discounted-price`}
            sx={{
              color: '#7527B9',
            }}
          >
            {getFormattedDiscountedPrice(discountedPrice, showNoCostAsWaived)}
          </Typography>
        ) : (
          <></>
        )}
      </Stack>
    ) : (
      <Typography className={`${productType}-cost ${className}-cost`} variant="b1Regular">
        {numberHelper.formatMoney(price ?? 0)}
      </Typography>
    );

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Stack
          direction="row"
          alignItems="flex-start"
          sx={{ minWidth: 0, flex: 1, flexWrap: isBenefitLevel ? { xs: 'wrap', sm: 'nowrap' } : 'nowrap' }}
        >
          <Typography variant="b1Regular" sx={{ '& sup': { verticalAlign: 'top' } }}>
            <Typography
              component="span"
              className={`${className}-title`}
              variant="b1Regular"
              sx={{ overflowWrap: 'anywhere' }}
              data-di-mask={`${mask ? 'true' : 'false'}`}
            >
              {title}
            </Typography>
            {subtitle && (
              <>
                {' '}
                <Typography
                  component="span"
                  className={`${className}-subtitle`}
                  variant="b3Regular"
                  sx={{
                    display: isBenefitLevel ? { xs: 'block', sm: 'inline' } : 'inline',
                    whiteSpace: isBenefitLevel ? 'normal' : 'nowrap',
                  }}
                >
                  - {subtitle}
                </Typography>
              </>
            )}
          </Typography>
        </Stack>

        {priceSection}
        {members && members.length > 0 && (
          <Box>
            {members.map((member, index) => (
              <Box key={index}>
                <Typography sx={{}} variant="b1Regular" data-di-mask={`${mask ? 'true' : 'false'}`}>
                  • {member.memberName} - <Typography variant="b3Regular">{member.memberType}</Typography>
                </Typography>
                {price &&
                  (discountedPrice && discountedPrice < price ? (
                    <Stack>
                      <Typography variant="b3Regular" sx={{ color: '#9E9E9E', textDecorationLine: 'line-through' }}>
                        {numberHelper.formatMoney(price)}
                      </Typography>
                      <Typography variant="b3Regular" sx={{ color: '#7527B9' }}>
                        {getFormattedDiscountedPrice(discountedPrice)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="b1Regular">{numberHelper.formatMoney(price)}</Typography>
                  ))}
              </Box>
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
};
